import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { Transform } from '@atlaskit/editor-prosemirror/transform';

import type { AiSuggestionsPlugin } from '../aiSuggestionsPluginType';
import { aiSuggestionsPluginKey } from '../pm-plugins/main';
import { addNoDiffAnalytics } from '../ui/utils/analytics';
import { applyToolCalls } from '../ui/utils/toolCalls/applyToolCalls';

import type { SelectSuggestionAction } from './types';

export const selectSuggestionAction: (
	api: ExtractInjectionAPI<AiSuggestionsPlugin> | undefined,
) => SelectSuggestionAction =
	(api: ExtractInjectionAPI<AiSuggestionsPlugin> | undefined) =>
	(id: string | null, options?: { hideDeletedDiffs?: boolean }): boolean => {
		api?.core?.actions.execute(({ tr }) => {
			tr.setMeta(aiSuggestionsPluginKey, { action: 'SELECT_SUGGESTION', id: id ?? undefined });

			if (id === null) {
				// Deselecting — hide the diff
				api?.showDiff?.commands.hideDiff?.({ tr });
			} else {
				// Selecting — show the diff for this suggestion
				const suggestion = api?.aiSuggestions?.sharedState.currentState()?.suggestionById.get(id);
				if (suggestion) {
					// Apply tool calls to an isolated transaction so we can read its `steps`
					// and `before` for diff visualisation without affecting the source transaction.
					const isolatedTr = new Transform(tr.doc);
					const appliedTr = applyToolCalls({ tr: isolatedTr, suggestion });

					//fire analytics if there is no diff
					if (appliedTr.before.eq(appliedTr.doc)) {
						addNoDiffAnalytics(api?.analytics?.actions)(tr, suggestion);
					}

					api?.showDiff?.commands.showDiff({
						// For a multi-step transaction, each step's `from`/`to` are
						// in the coordinate space of the doc BEFORE that specific
						// step is applied — i.e. `appliedTr.docs[i]`, not the
						// initial `appliedTr.before`. Inverting every step against
						// `appliedTr.before` throws `RangeError` (or silently
						// produces broken inverted steps) for any step past index 0.
						// Reverse the inverted-step array so they apply back to the
						// original order when the diff plugin replays them on
						// `appliedTr.doc`.
						steps: appliedTr.steps.map((step, i) => step.invert(appliedTr.docs[i])).reverse(),
						originalDoc: appliedTr.doc,
						isInverted: true,
						diffType: 'step',
						hideDeletedDiffs: options?.hideDeletedDiffs,
						scrollIntoView: true,
					})({ tr });
				}
			}

			return tr;
		});
		return true;
	};
